import React, { memo } from 'react';
import { useSelector } from 'react-redux';

import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
} from '@mui/material';
import ScrollableTooltipContent from '@sharedCR/common/ScrollableTooltipContent';
import TooltipWrapper from 'app/shared/components/common/TooltipWrapper/TooltipWrapper';
import InfoIcon from 'app/shared/componentsRestyled/coreDesignSystem/icons/InfoIcon';

import { IconSize } from 'constants/coreDesignSystem';
import { currencyOptions } from 'constants/loanFee';
import { formatNumberToCurrencyString } from 'utils/number';

import {
  selectWireAmountData,
  selectWireAmountRows,
} from 'store/loanApplications/selectors/closing.selectors';

import {
  boxStyles,
  footerAmountRowStyles,
  footerNoteStyles,
  footerStyles,
  rowCellStyles,
  tableBodyStyles,
  tableContainerStyles,
  titleHeadingStyles,
  titleStyles,
  tooltipStyles,
} from './WireAmountLabel.styles';

const WireAmountLabel = () => {
  const { calculatedWireAmount } = useSelector(selectWireAmountData);
  const rows = useSelector(selectWireAmountRows);

  return (
    <TooltipWrapper
      disableInteractive={false}
      title={
        <ScrollableTooltipContent
          footer={
            <Box sx={footerStyles}>
              <Box sx={footerAmountRowStyles}>
                <Typography variant="medium16">Calculated Wire Amount</Typography>
                <Typography color="primary.main" variant="bold16">
                  {formatNumberToCurrencyString(calculatedWireAmount ?? 0, currencyOptions)}
                </Typography>
              </Box>
              <Box sx={footerNoteStyles}>
                <InfoIcon size={IconSize[12]} />
                <Typography color="text.secondary" variant="regular12">
                  Updates automatically when loan terms change
                </Typography>
              </Box>
            </Box>
          }
        >
          <Box sx={titleStyles}>
            <Box sx={titleHeadingStyles}>
              <Typography variant="bold20">Wire Amount Calculation</Typography>
              <Typography color="text.secondary" variant="regular12">
                Auto-calculated
              </Typography>
            </Box>

            <TableContainer sx={tableContainerStyles}>
              <Table>
                <TableBody sx={tableBodyStyles}>
                  {rows.map(({ label, value }) => (
                    <TableRow className="table-row" key={label}>
                      <TableCell className="table-cell" sx={rowCellStyles}>
                        <Typography color="text.secondary" variant="regular14">
                          {label}
                        </Typography>
                      </TableCell>
                      <TableCell align="right" className="table-cell" sx={rowCellStyles}>
                        <Typography color="text.secondary" variant="regular14">
                          {formatNumberToCurrencyString(value ?? 0, currencyOptions)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </ScrollableTooltipContent>
      }
      tooltipSx={tooltipStyles}
    >
      <Box sx={boxStyles}>
        <Typography variant="regular14">Wire Amount</Typography>
        <InfoIcon size={IconSize[16]} />
      </Box>
    </TooltipWrapper>
  );
};

export default memo(WireAmountLabel);
